import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { parseUsdc, pctToBps, bpsToPctStr } from '../../utils/format';
import { RANK_NAMES, NUM_RANKS, NUM_MILESTONES, NUM_LEVELS } from '../../utils/constants';
import { sendTx } from '../../utils/txHelpers';

export const RankLevelManager = () => {
  const { viewsR, adminW } = useContracts();
  const notify = useNotify();
  const [busy, setBusy] = useState(null);

  // Rank state
  const [rIdx, setRIdx] = useState(0);
  const [rSelf, setRSelf] = useState('');
  const [rDir, setRDir] = useState('');
  const [rTeam, setRTeam] = useState('');
  const [rLegRank, setRLegRank] = useState(0);
  const [rLegCount, setRLegCount] = useState(0);

  // Level state (input as % now)
  const [lIdx, setLIdx] = useState(0);
  const [lPct, setLPct] = useState('');
  const [lReq, setLReq] = useState('');

  // Milestone state
  const [mIdx, setMIdx] = useState(0);
  const [mMem, setMMem] = useState('');
  const [mVol, setMVol] = useState('');

  // Rank pool percentages (inputs as %)
  const [poolPcts, setPoolPcts] = useState(['0.4', '0.6', '0.8', '1.0', '1.2']);

  // ROI timeframe (input as hours for friendlier UX)
  const [roiHours, setRoiHours] = useState('24');

  // Load current rank pool percentages to prefill
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        const bps = await viewsR.getRankPoolPercentages();
        if (cancelled) return;
        setPoolPcts(bps.map((b) => bpsToPctStr(b)));
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR]);

  const runTx = async (label, method, args) => {
    if (!adminW) { notify.error('Wallet not ready'); return; }
    setBusy(label);
    try {
      await sendTx(adminW, method, args, { notify, label });
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(null);
    }
  };

  // ============ RANK ============
  const updateRank = () => {
    if (!rSelf || !rDir || !rTeam) { notify.error('Fill self / directs / team volume'); return; }
    let selfBn, teamBn;
    try {
      selfBn = parseUsdc(rSelf);
      teamBn = parseUsdc(rTeam);
    } catch (e) { notify.error('Invalid amount'); return; }
    runTx('rank', 'updateRankRequirement', [rIdx, selfBn, rDir, teamBn, rLegRank, rLegCount]);
  };

  // ============ LEVEL ============
  const updateLevel = () => {
    const bps = pctToBps(lPct);
    if (bps == null) { notify.error('Invalid percentage'); return; }
    if (bps > 10000) { notify.error('Level % cannot exceed 100%'); return; }
    if (lReq === '') { notify.error('Enter required directs'); return; }
    runTx('level', 'updateLevelConfig', [lIdx, bps, lReq]);
  };

  // ============ MILESTONE ============
  const updateMilestone = () => {
    if (!mMem || !mVol) { notify.error('Fill members / volume'); return; }
    let volBn;
    try { volBn = parseUsdc(mVol); } catch (e) { notify.error('Invalid amount'); return; }
    runTx('ms', 'updateMilestoneRequirement', [mIdx, mMem, volBn]);
  };

  // ============ RANK POOL BPS ============
  const updatePoolBps = () => {
    const bps = poolPcts.map((p) => pctToBps(p));
    if (bps.some((b) => b == null)) { notify.error('Invalid percentage in one of the fields'); return; }
    runTx('bps', 'updateRankPoolBps', [bps]);
  };
  const poolSum = poolPcts.reduce((acc, p) => acc + (Number(p) || 0), 0);

  // ============ ROI TIMEFRAME ============
  const updateROITimeframe = () => {
    const h = Number(roiHours);
    if (!h || h <= 0) { notify.error('Invalid hours'); return; }
    const secs = Math.round(h * 3600);
    runTx('roi', 'updateROITimeframe', [secs]);
  };

  return (
    <div>
      {/* RANKS */}
      <div className="card mb-24">
        <div className="section-title">Rank Requirements</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Rank</label>
            <select className="select" value={rIdx} onChange={(e) => setRIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_RANKS }).map((_, i) => (
                <option key={i} value={i}>{i} — {RANK_NAMES[i + 1]}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Self Investment (USDC)</label>
            <input className="input" type="number" value={rSelf} onChange={(e) => setRSelf(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Active Directs</label>
            <input className="input" type="number" value={rDir} onChange={(e) => setRDir(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Team Volume (USDC)</label>
            <input className="input" type="number" value={rTeam} onChange={(e) => setRTeam(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Leg Rank (0 = none)</label>
            <input className="input" type="number" value={rLegRank} onChange={(e) => setRLegRank(Number(e.target.value))} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Leg Count</label>
            <input className="input" type="number" value={rLegCount} onChange={(e) => setRLegCount(Number(e.target.value))} />
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'rank'} onClick={updateRank}>
          {busy === 'rank' && <ButtonSpinner />}Update Rank
        </button>
      </div>

      {/* LEVELS */}
      <div className="card mb-24">
        <div className="section-title">Level Config</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Level</label>
            <select className="select" value={lIdx} onChange={(e) => setLIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_LEVELS }).map((_, i) => (
                <option key={i} value={i}>L{i + 1}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Commission (%)</label>
            <input className="input" type="number" step="0.01" placeholder="20" value={lPct} onChange={(e) => setLPct(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Active Directs</label>
            <input className="input" type="number" value={lReq} onChange={(e) => setLReq(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'level'} onClick={updateLevel}>
          {busy === 'level' && <ButtonSpinner />}Update Level
        </button>
      </div>

      {/* MILESTONES */}
      <div className="card mb-24">
        <div className="section-title">Milestone Requirements</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Milestone #</label>
            <select className="select" value={mIdx} onChange={(e) => setMIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_MILESTONES }).map((_, i) => (
                <option key={i} value={i}>M{i + 1}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Active Members</label>
            <input className="input" type="number" value={mMem} onChange={(e) => setMMem(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Team Volume (USDC)</label>
            <input className="input" type="number" value={mVol} onChange={(e) => setMVol(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'ms'} onClick={updateMilestone}>
          {busy === 'ms' && <ButtonSpinner />}Update Milestone
        </button>
      </div>

      {/* RANK POOL BPS */}
      <div className="card mb-24">
        <div className="section-title">Rank Pool Percentages (must sum to the Rank Pool total fee)</div>
        <div className="admin-form-grid">
          {poolPcts.map((v, i) => (
            <div className="input-group" key={i}>
              <label className="input-label">{RANK_NAMES[i + 1]} (%)</label>
              <input className="input" type="number" step="0.01" value={v}
                onChange={(e) => setPoolPcts(poolPcts.map((x, j) => j === i ? e.target.value : x))} />
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Sum: <b>{poolSum.toFixed(2)}%</b> — must equal the Rank Pool total fee configured above.
        </div>
        <button className="btn btn-primary" disabled={busy === 'bps'} onClick={updatePoolBps}>
          {busy === 'bps' && <ButtonSpinner />}Update Pool %
        </button>
      </div>

      {/* ROI TIMEFRAME */}
      <div className="card">
        <div className="section-title">ROI Period</div>
        <div className="input-group">
          <label className="input-label">Hours per ROI period (default: 24 = daily)</label>
          <input className="input" type="number" step="0.01" value={roiHours} onChange={(e) => setRoiHours(e.target.value)} />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            = {Math.round((Number(roiHours) || 0) * 3600)} seconds on-chain
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy === 'roi' || !roiHours} onClick={updateROITimeframe}>
          {busy === 'roi' && <ButtonSpinner />}Update Timeframe
        </button>
      </div>
    </div>
  );
};
